import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import api from '@/lib/axios';
import { rangoDeHoy } from '@/lib/tareas';
import { useAsignables, useEtiquetas, useTableros, useTareasLista } from '@/hooks/useTareas';
import { PRIORIDADES, type FiltrosTareas, type PrioridadTarea } from '@/types/tareas';
import { AvatarsAsignados, PrioridadBadge, RelacionChip, VencimientoTexto } from '@/components/tareas/TareaPiezas';

const campo = 'px-2.5 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';
const POR_PAGINA = 25;
const DEBOUNCE_MS = 350;

type Vencimiento = '' | 'vencidas' | 'hoy' | 'semana' | 'sin_fecha';

export function ListaTareasView({ onAbrirTarea }: { onAbrirTarea: (id: string) => void }) {
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [tableroId, setTableroId] = useState('');
  const [asignadoId, setAsignadoId] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadTarea | ''>('');
  const [etiquetaId, setEtiquetaId] = useState('');
  const [consultorioId, setConsultorioId] = useState('');
  const [vencimiento, setVencimiento] = useState<Vencimiento>('');
  const [estado, setEstado] = useState<'no' | 'si' | ''>('no');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setQ(busqueda.trim()); setPage(1); }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [busqueda]);

  const { data: tablerosData } = useTableros();
  const { data: asignables = [] } = useAsignables();
  const { data: etiquetas = [] } = useEtiquetas();
  const { data: consultorios = [] } = useQuery<any[]>({
    queryKey: ['consultorios'],
    queryFn: () => api.get('/api/clinica/consultorios').then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const filtros = useMemo<FiltrosTareas>(() => {
    const f: FiltrosTareas = { page, limit: POR_PAGINA, q: q || undefined };
    if (tableroId) f.tableroId = tableroId;
    if (asignadoId) f.asignadoId = asignadoId;
    if (prioridad) f.prioridad = prioridad;
    if (etiquetaId) f.etiquetaId = etiquetaId;
    if (consultorioId) f.consultorioId = consultorioId;
    if (estado) f.completadas = estado;
    const { hoyInicio, hoyFin, semanaFin } = rangoDeHoy();
    if (vencimiento === 'vencidas') f.vencidas = true;
    if (vencimiento === 'hoy') { f.vencimientoDesde = hoyInicio.toISOString(); f.vencimientoHasta = hoyFin.toISOString(); }
    if (vencimiento === 'semana') { f.vencimientoDesde = hoyInicio.toISOString(); f.vencimientoHasta = semanaFin.toISOString(); }
    if (vencimiento === 'sin_fecha') f.sinFecha = true;
    return f;
  }, [page, q, tableroId, asignadoId, prioridad, etiquetaId, consultorioId, estado, vencimiento]);

  const { data, isLoading, isFetching } = useTareasLista(filtros);
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const cambiar = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[12rem] max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por título" className={`${campo} pl-8 w-full`} />
        </div>
        <select value={tableroId} onChange={e => cambiar(setTableroId)(e.target.value)} className={campo}>
          <option value="">Todos los tableros</option>
          {(tablerosData?.tableros ?? []).map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
        <select value={asignadoId} onChange={e => cambiar(setAsignadoId)(e.target.value)} className={campo}>
          <option value="">Cualquier asignado</option>
          {asignables.map(u => <option key={u.id} value={u.id}>{[u.nombre, u.apellidoPaterno].filter(Boolean).join(' ')}</option>)}
        </select>
        <select value={prioridad} onChange={e => cambiar(setPrioridad)(e.target.value as PrioridadTarea | '')} className={campo}>
          <option value="">Toda prioridad</option>
          {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={etiquetaId} onChange={e => cambiar(setEtiquetaId)(e.target.value)} className={campo}>
          <option value="">Toda etiqueta</option>
          {etiquetas.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
        <select value={vencimiento} onChange={e => cambiar(setVencimiento)(e.target.value as Vencimiento)} className={campo}>
          <option value="">Cualquier vencimiento</option>
          <option value="vencidas">Vencidas</option>
          <option value="hoy">Vencen hoy</option>
          <option value="semana">Próximos 7 días</option>
          <option value="sin_fecha">Sin fecha</option>
        </select>
        {consultorios.length > 1 && (
          <select value={consultorioId} onChange={e => cambiar(setConsultorioId)(e.target.value)} className={campo}>
            <option value="">Todos los consultorios</option>
            {consultorios.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        )}
        <select value={estado} onChange={e => cambiar(setEstado)(e.target.value as 'no' | 'si' | '')} className={campo}>
          <option value="no">Abiertas</option>
          <option value="si">Completadas</option>
          <option value="">Todas</option>
        </select>
      </div>

      <div className="rounded-xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[44rem]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-text-secondary border-b border-border">
              <th className="px-4 py-2.5 font-semibold">Tarea</th>
              <th className="px-3 py-2.5 font-semibold">Estado</th>
              <th className="px-3 py-2.5 font-semibold">Prioridad</th>
              <th className="px-3 py-2.5 font-semibold">Vencimiento</th>
              <th className="px-3 py-2.5 font-semibold">Asignados</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="py-12 text-center text-gray-400"><Loader2 className="animate-spin inline" /></td></tr>
            )}
            {!isLoading && !data?.items.length && (
              <tr><td colSpan={5} className="py-12 text-center text-text-secondary">No hay tareas con estos filtros.</td></tr>
            )}
            {data?.items.map(t => (
              <tr key={t.id} onClick={() => onAbrirTarea(t.id)} className="border-b border-border last:border-0 hover:bg-primary-50/40 cursor-pointer">
                <td className="px-4 py-2.5 max-w-md">
                  <p className="font-medium text-text-primary truncate">
                    {t.parentId && <span className="text-text-secondary font-normal">↳ </span>}{t.titulo}
                  </p>
                  {t.relaciones.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.relaciones.slice(0, 3).map(r => <RelacionChip key={r.id} relacion={r} compacta />)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-text-secondary whitespace-nowrap">{t.tableroNombre} · {t.columnaNombre}</td>
                <td className="px-3 py-2.5"><PrioridadBadge prioridad={t.prioridad} /></td>
                <td className="px-3 py-2.5 whitespace-nowrap"><VencimientoTexto tarea={t} />{!t.fechaVencimiento && <span className="text-xs text-text-secondary">—</span>}</td>
                <td className="px-3 py-2.5"><AvatarsAsignados asignados={t.asignados} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-text-secondary">
        <span>{data ? `${data.total} tarea${data.total === 1 ? '' : 's'}` : ''}{isFetching && !isLoading ? ' · actualizando…' : ''}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50" aria-label="Página anterior">
            <ChevronLeft size={16} />
          </button>
          <span>Página {page} de {totalPaginas}</span>
          <button disabled={page >= totalPaginas} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50" aria-label="Página siguiente">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
